import { create } from '@thoughtspot/logger';
import { getSpecialFormatData } from '@thoughtspot/special-data-value-format';
import { getId, getTrueEffectiveAggregateType, isAttribute, isEffectivelyNonNumeric, isGroupingColumn, } from '@thoughtspot/visualization-column-util';
import { getColumns, getVisualizedColumns } from '../../core/chart-model-utils';
import { getName as getColumnName } from '../../core/column-name-util';
import { getOverrideNamesForChartSpecificColumns } from '../../core/measure-names-and-values';
import { generateQueryDefinitions, getDataArray } from '../../data/data-util';
import { ChartDataModel } from '../../highcharts/highchart-data-model';
import { ColumnAggregationType, } from '../../types';
export class BaseTableDataModel extends ChartDataModel {
    constructor(chartModel, dataArray, queryDefinitions, envOptions, renderOptions, getEventMap) {
        super();
        this.chartModel = chartModel;
        this.dataArray = dataArray;
        this.queryDefinitions = queryDefinitions;
        this.envOptions = envOptions;
        this.renderOptions = renderOptions;
        this.getEventMap = getEventMap;
        this._columnIdToColumn = {};
        this._columnNameToColumn = {};
        this._colIdToUniqueName = {};
        this.logger = create('dx-table-data-model');
        BaseTableDataModel.blinkAggTypesToPivotAggTypes = {
            [ColumnAggregationType.Sum]: 'sum',
            [ColumnAggregationType.Count]: 'sum',
            [ColumnAggregationType.CountDistinct]: 'sum',
            [ColumnAggregationType.None]: 'sum',
            [ColumnAggregationType.Average]: 'avg',
            [ColumnAggregationType.Min]: 'min',
            [ColumnAggregationType.Max]: 'max',
        };
        this._rawData = dataArray;
        this.parseDefinitions(chartModel);
        if (getVisualizedColumns(chartModel).length === 0) {
            this._data = [];
        }
        else {
            this.processData(chartModel, dataArray);
        }
    }
    getDataArray() {
        return getDataArray(this._rawData);
    }
    get hasNoData() {
        return this._count === 0;
    }
    get hasMoreData() {
        this.logger.info('pending implementation in v2');
        return false;
    }
    get count() {
        return this._count;
    }
    get data() {
        return this._rawData;
    }
    get columns() {
        return this._columns;
    }
    get columnIdToColumn() {
        return this._columnIdToColumn;
    }
    get columnNameToColumn() {
        return this._columnNameToColumn;
    }
    getColumnById(id) {
        const column = this._columnIdToColumn[id];
        if (!column && id) {
            this.logger.error('unknown column', id);
        }
        return column;
    }
    columnCanContainsLink(id) {
        const column = this.getColumnById(id);
        return column && isAttribute(column) && isEffectivelyNonNumeric(column);
    }
    getEffectiveAggrType(col) {
        const aggType = getTrueEffectiveAggregateType(col);
        return BaseTableDataModel.blinkAggTypesToPivotAggTypes[aggType];
    }
    getDataRowAttribute(data) {
        const specialFormat = getSpecialFormatData(data);
        data = specialFormat || `${data}`;
        return data;
    }
    parseDefinitions(chartModel) {
        this.buildColumnMaps(chartModel);
    }
    processData(chartModel, dataArray) {
        if (!dataArray) {
            return;
        }
        const data = this.getBaseQueryDataSet(dataArray);
        if (!data) {
            return;
        }
        const queryDefinitions = generateQueryDefinitions(chartModel);
        const columnsInDataOrder = (queryDefinitions.length >
            0
            ? queryDefinitions[0].getColumnsInDataOrder()
            : getVisualizedColumns(chartModel));
        this._dataRowIndexToColumn = columnsInDataOrder.reduce((dataRowIndexToColumn, column) => {
            dataRowIndexToColumn[column.dataRowIndex] = column;
            return dataRowIndexToColumn;
        }, {});
        this._data = data.map(dataRow => {
            return this.getDataRow(dataRow);
        });
        this._count = this._data.length;
    }
    getBaseQueryDataSet(dataArray) {
        return dataArray[0].data;
    }
    buildColumnMaps(chartModel) {
        const columns = getColumns(chartModel);
        const chartSpecificColumnNames = getOverrideNamesForChartSpecificColumns(chartModel);
        this._colIdToUniqueName = columns.reduce((acc, column) => {
            const id = getId(column);
            const name = chartSpecificColumnNames[id] || getColumnName(column);
            acc[id] = name;
            return acc;
        }, {});
        this._columnIdToColumn = columns.reduce((map, col) => {
            const name = getId(col);
            map[name] = col;
            this._columnNameToColumn[this._colIdToUniqueName[name]] = col;
            return map;
        }, {});
    }
    getDataRow(dataRow) {
        return dataRow.reduce((jsonObject, data, idx) => {
            const column = this._dataRowIndexToColumn[idx];
            if (column) {
                const colId = getId(column);
                const isColAttr = isEffectivelyNonNumeric(column) || isGroupingColumn(column);
                if (this.getDataRowMeasureAndAttribute) {
                    data = this.getDataRowMeasureAndAttribute(data, colId, this.getDataRowAttribute, isColAttr);
                }
                jsonObject[colId] = data;
            }
            return jsonObject;
        }, {});
    }
}
//# sourceMappingURL=dx-table-data-model.js.map